import { Store, UserDetails } from '../models/UserModel';
import { KPI_FORMAT } from '../constants/constants';
import { DynamicBaseUrlType, DynamicUrlField } from '../constants/urlPaths';
import React from 'react';
import { GridRenderCellParams } from '@mui/x-data-grid';
import type { ValueFormat } from '../models/LineGraphModels';
import { env } from '../env';

/**
 * Returns className for DataGrid rows based on alternating pattern
 *
 * @param params - GridRowParams from MUI DataGrid
 * @param evenRowClass - CSS class for even rows (index 0, 2, 4...)
 * @param oddRowClass - CSS class for odd rows (index 1, 3, 5...)
 * @returns CSS class name string
 *
 * @example
 * getAlternatingRowClassName(params, styles.evenRow, styles.oddRow)
 */
export const getAlternatingRowClassName = (
  params: { indexRelativeToCurrentPage: number },
  evenRowClass: string,
  oddRowClass: string,
): string => {
  return params.indexRelativeToCurrentPage % 2 === 0 ? evenRowClass : oddRowClass;
};

/**
 * Converts a string value to a boolean
 *
 * @param value - String value to convert (accepts "true", "1", or null)
 * @returns Boolean representation of the string value
 *
 * @example
 * stringToBoolean("true") // returns true
 * stringToBoolean("false") // returns false
 * stringToBoolean("1") // returns true
 * stringToBoolean(null) // returns false
 */
export const stringToBoolean = (value: string | null): boolean => {
  if (value === null) return false;
  return value?.toLowerCase() === 'true' || value === '1';
};

/**
 * Sorts a list of stores by store number in ascending order
 *
 * @param stores - Array of Store objects to sort
 * @returns A new sorted array of stores
 *
 * @example
 * sortStoreList([{ storeNumber: "100" }, { storeNumber: "50" }])
 * // returns [{ storeNumber: "50" }, { storeNumber: "100" }]
 */
export const sortStoreList = (stores: Store[]): Store[] => {
  return [...stores].sort(
    (a, b) => a.storeNumber.localeCompare(b.storeNumber, undefined, { numeric: true }), // numeric true allows for numeric sorting of string values
  );
};

/**
 * Gets the effective store number based on multi-store mode
 * In multi-store mode, returns the parent store from the user's chain list
 * In single-store mode, returns the current store number
 *
 * @param userDetails - User details containing store information
 * @param isMultiStoreEnabled - Whether multi-store mode is enabled
 * @returns The effective store number to use for API calls
 *
 * @example
 * getEffectiveStoreNumber(userDetails, true)  // Returns parent store in multi-store mode
 * getEffectiveStoreNumber(userDetails, false) // Returns current store in single-store mode
 */
export const getEffectiveStoreNumber = (
  userDetails: UserDetails | null,
  isMultiStoreEnabled: boolean,
): string => {
  if (!userDetails?.currentStoreNumber) return '';

  return isMultiStoreEnabled
    ? userDetails?.userChainList?.[0]?.parentStore || userDetails.currentStoreNumber
    : userDetails.currentStoreNumber;
};

/**
 * Gets the current store details from the store list based on the current store number
 *
 * @param currentStoreNumber - The current store number to find
 * @param storeList - Array of Store objects to search
 * @returns The matching Store object or null if not found
 *
 * @example
 * getCurrentStoreDetails("56877", stores)
 * // returns { storeNumber: "56877", storeName: "...", ... }
 */
export const getCurrentStoreDetails = (
  currentStoreNumber: string | undefined,
  storeList: Store[] | undefined,
): Store | null => {
  if (!currentStoreNumber || !storeList) {
    return null;
  }
  return storeList.find(store => store.storeNumber === currentStoreNumber) || null;
};

/**
 * Format load date for display in uppercase format
 * Uses UTC to avoid timezone-related date shifts
 *
 * @param dateString - ISO date string to format
 * @param includeDay - Whether to include the day in the formatted date (default: true)
 * @param monthOffset - Number of months to add/subtract (positive = future, negative = past) (default: 0)
 * @param monthFormat - Month format: 'short' (e.g., "OCT") or 'long' (e.g., "OCTOBER") (default: 'short')
 * @returns Formatted date string in "MONTH DAY, YEAR" or "MONTH YEAR" format
 *
 * @example
 * formatLoadDate("2023-10-15") // returns "OCT 15, 2023"
 * formatLoadDate("2024-01-01") // returns "JAN 1, 2024"
 * formatLoadDate("2023-10-15", false) // returns "OCT 2023"
 * formatLoadDate("2026-03-15", false, -1, 'long') // returns "FEBRUARY 2026"
 * formatLoadDate("2026-03-15", false, 1) // returns "APR 2026"
 */
export const formatLoadDate = (
  dateString: string,
  includeDay = true,
  monthOffset = 0,
  monthFormat: 'short' | 'long' = 'short',
): string => {
  const date = new Date(dateString);

  // Apply month offset using UTC methods to maintain consistency with UTC timezone formatting
  if (monthOffset !== 0) {
    date.setUTCMonth(date.getUTCMonth() + monthOffset);
  }

  const options: Intl.DateTimeFormatOptions = {
    month: monthFormat,
    year: 'numeric',
    timeZone: 'UTC',
  };

  if (includeDay) {
    options.day = 'numeric';
  }

  if (monthFormat === 'long') {
    return date.toLocaleDateString('en-US', options);
  }
  return date.toLocaleDateString('en-US', options).toUpperCase();
};

/**
 * Returns true if the date string is a valid API date — non-empty and not
 * the C# DateTime.MinValue sentinel ("0001-01-01").
 */
export const isValidApiDate = (dateStr: string | null | undefined): boolean =>
  !!dateStr && !dateStr.startsWith('0001');

/**
 * Formats a date to "MMM DD, YYYY" format (e.g., "JAN 20, 2026")
 * @param date - Date object or ISO date string (YYYY-MM-DDTHH:MM:SS)
 * @returns Formatted date string
 */
export const formatDateRange = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  const month = dateObj.toLocaleString('en-US', { month: 'short' }).toUpperCase();
  const day = dateObj.getDate();
  const year = dateObj.getFullYear();

  return `${month} ${day}, ${year}`;
};

/**
 * Resolves a URL string that may be:
 * - A dynamic URL with format "dynamicURL:BASE_TYPE:FIELD" (e.g., "dynamicURL:WFC:current")
 * - A regular static URL
 * - Empty/null/undefined
 *
 * @param urlString - The URL string to resolve
 * @param urlCode - The URL code for URL resolution (required when using dynamic URLs)
 * @param buildDynamicUrl - Function to build dynamic URLs (from useBuildDynamicUrl hook or context)
 * @returns Resolved URL string or null if empty/invalid
 *
 * @example
 * resolveDynamicUrl("dynamicURL:WFC:current", "MGIS", buildDynamicUrl) // returns full WFC URL
 * resolveDynamicUrl("https://example.com", undefined, buildDynamicUrl) // returns "https://example.com"
 * resolveDynamicUrl("", "MGIS", buildDynamicUrl) // returns null
 */
export const resolveDynamicUrl = (
  urlString: string | undefined | null,
  urlCode?: string,
  buildDynamicUrl?: (
    baseUrlType: DynamicBaseUrlType,
    urlCode: string,
    field?: DynamicUrlField,
  ) => string | null,
): string | null => {
  if (!urlString?.trim()) {
    return null;
  }

  if (urlString.startsWith('dynamicURL:')) {
    if (!urlCode) {
      console.error('urlCode is required when using dynamic URLs');
      return null;
    }
    const parts = urlString.split(':');
    const baseUrlType = parts[1] as DynamicBaseUrlType;
    const field = parts[2] as DynamicUrlField;
    return buildDynamicUrl?.(baseUrlType, urlCode, field) ?? null;
  }

  return urlString;
};

/**
 * Formats a date to "MMM - YYYY" format (e.g., "JAN - 2026")
 * Used for Inventory Value graph
 */
export const formatMonthYearRange = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  const month = dateObj.toLocaleString('en-US', { month: 'short' }).toUpperCase();
  const year = dateObj.getFullYear();

  return `${month} - ${year}`;
};

/**
 * Detects if the current device is a mobile device
 *
 * @returns True if the user agent indicates a mobile device, false otherwise
 *
 * @example
 * isMobileDevice() // returns true on mobile devices
 */
export const isMobileDevice = (): boolean => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

/**
 * Determines if a URL points to an internal enterprise application.
 * Internal URLs bypass noopener/noreferrer restrictions to avoid redirect latency.
 *
 * @param url - The URL to check (can be null, undefined, or empty)
 * @returns true if the URL starts with any configured internal BASE_URL, false otherwise
 *
 * @example
 * ```typescript
 * isInternalEnterpriseUrl('https://qaadw.acehardware.com/reports') // true (WFC)
 * isInternalEnterpriseUrl('https://qaacenet.npaceservices.com/dashboard') // true (ACENET)
 * isInternalEnterpriseUrl('https://qaretailer.npaceservices.com/config') // true (RETAILER)
 * isInternalEnterpriseUrl('https://external-site.com') // false
 * isInternalEnterpriseUrl(null) // false
 * ```
 */
export function isInternalEnterpriseUrl(url: string | null | undefined): boolean {
  if (!url) {
    return false;
  }

  // Check against all configured internal enterprise base URLs
  const internalBaseUrls = [env.BASE_URL_WFC, env.BASE_URL_ACENET, env.BASE_URL_RETAILER];

  return internalBaseUrls.some(baseUrl => baseUrl && url.startsWith(baseUrl));
}

/**
 * Adds thousands separator (commas) to numbers for improved readability
 * Handles both string and number inputs from API responses
 * Formats decimals to 2 decimal places, integers without decimals
 *
 * @param value - Number or string representation of a number to format
 * @param forceDecimals - If true, always formats to 2 decimal places (including integers)
 * @returns Formatted string with commas as thousands separators
 *
 * @example
 * formatNumberWithCommas(1000) // returns "1,000"
 * formatNumberWithCommas(1000, true) // returns "1,000.00"
 * formatNumberWithCommas("1000000") // returns "1,000,000"
 * formatNumberWithCommas("839289.5") // returns "839,289.50"
 * formatNumberWithCommas(239.1) // returns "239.10"
 * formatNumberWithCommas(0) // returns "0"
 * formatNumberWithCommas(0, true) // returns "0.00"
 * formatNumberWithCommas("-123456") // returns "-123,456"
 * formatNumberWithCommas("N/A") // returns "N/A" (invalid numbers returned as-is)
 */
export const formatNumberWithCommas = (
  value: string | number,
  forceDecimals = false,
  decimalPlaces = 2,
): string => {
  const stringValue = typeof value === 'number' ? value.toString() : value;

  const numericValue = parseFloat(stringValue);

  // Exit the function and return original if not a valid number
  if (isNaN(numericValue)) return stringValue;

  // Format to specified decimal places if forceDecimals is true, otherwise preserve as-is
  const formattedValue = forceDecimals
    ? numericValue.toFixed(decimalPlaces)
    : stringValue.replace(/,/g, '');
  const parts = formattedValue.split('.');
  const integerPart = parts[0];
  const decimalPart = parts.length > 1 ? '.' + parts[1] : '';

  // Add commas to integer part using native toLocaleString
  const withCommas = parseInt(integerPart, 10).toLocaleString('en-US');

  return withCommas + decimalPart;
};

/**
 * Rounds percentage values to one decimal place for consistent display
 * Handles both string and number inputs from API responses
 *
 * @param value - Number or string representation of a percentage value
 * @returns String formatted to one decimal place
 *
 * @example
 * roundToOneDecimal(39.72) // returns "39.7"
 * roundToOneDecimal("39.75") // returns "39.8"
 * roundToOneDecimal("12.999") // returns "13.0"
 * roundToOneDecimal("abc") // returns "abc" (invalid numbers returned as-is)
 */
export const roundToOneDecimal = (value: string | number): string => {
  const stringValue = typeof value === 'number' ? value.toString() : value;
  const numericValue = parseFloat(stringValue);
  if (isNaN(numericValue)) return stringValue;

  // Round to one decimal place using absolute value to correctly handle negative .5 cases
  return ((Math.sign(numericValue) * Math.round(Math.abs(numericValue) * 10)) / 10).toFixed(1);
};

/**
 * Rounds a numeric value to the nearest whole number
 * Handles both string and number inputs from API responses
 * Used for currency values that should display without decimal places
 *
 * @param value - Number or string representation of a number to round
 * @returns Rounded whole number, or 0 if value is invalid
 *
 * @example
 * roundToWholeNumber(1234.56) // returns 1235
 * roundToWholeNumber("1234.78") // returns 1235
 * roundToWholeNumber("-1234.4") // returns -1234
 * roundToWholeNumber("abc") // returns 0
 */
export const roundToWholeNumber = (value: string | number): number => {
  const numericValue = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(numericValue)) return 0;
  return Math.round(numericValue);
};

/**
 * Detects if a numeric value is non-zero but would round to zero at the given precision
 * Used to determine if special formatting (like < 0.1) should be applied to change values
 *
 * @param value - The numeric value to check
 * @param decimalPlaces - Number of decimal places used for rounding (e.g., 1 for one decimal, 2 for two decimals)
 * @returns True if value is non-zero but would round to 0 at the given precision, false otherwise
 *
 * @example
 * isSmallNonZeroValue(0.04, 1) // returns true (0.04 rounds to 0.0 with 1 decimal)
 * isSmallNonZeroValue(0.08, 1) // returns false (0.08 rounds to 0.1 with 1 decimal)
 * isSmallNonZeroValue(0.3, 0) // returns true (0.3 rounds to 0 with 0 decimals)
 * isSmallNonZeroValue(0, 1) // returns false (actual zero, not a small value)
 */
export const isSmallNonZeroValue = (value: number, decimalPlaces: number): boolean => {
  if (value === 0) return false;
  const threshold = 0.5 * Math.pow(10, -decimalPlaces);
  return Math.abs(value) < threshold;
};

/**
 * Formats small non-zero values that would round to zero using bounded notation (< or >)
 * This provides transparency when values exist but are too small to display precisely
 *
 * @param value - The numeric value to format
 * @param format - Format type: 'DOL' (currency), 'PCT' (percentage), or 'VAL' (plain value)
 * @param decimalPlaces - Number of decimal places for precision (determines the display threshold)
 * @returns Formatted string with < or > notation (e.g., "<$1", "<0.1", ">-0.1")
 *
 * @example
 * formatSmallChangeValue(0.04, 'PCT', 1) // returns "<0.1"
 * formatSmallChangeValue(-0.04, 'PCT', 1) // returns ">-0.1"
 * formatSmallChangeValue(0.3, 'DOL', 0) // returns "<$1"
 * formatSmallChangeValue(-0.3, 'DOL', 0) // returns ">-$1"
 * formatSmallChangeValue(0.004, 'VAL', 2) // returns "<0.01"
 */
export const formatSmallChangeValue = (
  value: number,
  format: ValueFormat,
  decimalPlaces: number,
): string => {
  const minDisplayable = Math.pow(10, -decimalPlaces).toFixed(decimalPlaces);
  const isPositive = value > 0;

  if (format === 'DOL') {
    return isPositive ? `<$${minDisplayable}` : `>-$${minDisplayable}`;
  } else if (format === 'PCT') {
    return isPositive ? `<${minDisplayable}%` : `>-${minDisplayable}%`;
  }
  // VAL format
  return isPositive ? `<${minDisplayable}` : `>-${minDisplayable}`;
};

/**
 * Determines if a fixed column label represents a currency-based value
 * Used to decide whether to display $ symbol in data grid cells
 *
 * @param fixedColumnLabel - Name of the fixed column label (e.g., "Metric", "Category", "KPI")
 * @param currencyKeywords - Array of keywords that identify currency values (can include symbols like '$')
 * @returns True if row represents currency data, false otherwise
 *
 * @example
 * isCurrencyFixedColumn("Sales", ["sales", "profit", "$"]) // returns true
 * isCurrencyFixedColumn("Gross Profit $", ["sales", "$"]) // returns true
 * isCurrencyFixedColumn("Transactions", ["sales"]) // returns false
 */
export const isCurrencyFixedColumn = (
  fixedColumnLabel: string,
  currencyKeywords: string[],
): boolean => {
  // Check if label includes any of the provided keywords (case-insensitive, including symbols like '$')
  return currencyKeywords.some(keyword =>
    fixedColumnLabel.toLowerCase().includes(keyword.toLowerCase()),
  );
};

/**
 * Determines if a fixed column label represents a percentage-based value
 * Used to decide whether to display % symbol in data grid cells
 *
 * @param fixedColumnLabel - Name of the fixed column label (e.g., "Metric", "Category", "KPI")
 * @returns True if row represents percentage data, false otherwise
 *
 * @example
 * isPercentageFixedColumn("Gross Margin %") // returns true
 * isPercentageFixedColumn("margin") // returns true
 * isPercentageFixedColumn("Sales") // returns false
 * isPercentageFixedColumn("Transactions") // returns false
 */
export const isPercentageFixedColumn = (fixedColumnLabel: string): boolean => {
  return fixedColumnLabel.includes('%') || fixedColumnLabel.toLowerCase().includes('margin');
};

/**
 * Maps performance status values to corresponding color codes for visual indicators
 * Provides consistent color-coding across all data grids and performance displays
 * Handles case-insensitive status values and unknown statuses gracefully
 *
 * @param statusValue - Status string from API (e.g., "positive", "negative", "neutral")
 * @returns Hex color code or 'inherit' for default styling
 *
 * @example
 * getColorByStatus("positive") // returns "#43A047" (green)
 * getColorByStatus("NEGATIVE") // returns "#D32F2F" (red)
 * getColorByStatus("neutral") // returns "inherit"
 * getColorByStatus("unknown") // returns "inherit" (fallback for unknown statuses)
 */
export const changeColumnColorStatus = (statusValue: string): string => {
  // Normalize status to lowercase for case-insensitive comparison
  const normalizedStatus = statusValue?.toLowerCase().trim();

  switch (normalizedStatus) {
    case 'positive':
      return '#43A047'; // Green for positive values
    case 'negative':
      return '#D32F2F'; // Red for negative values
    case 'neutral':
      return 'inherit'; // Default color for neutral/no change
    default:
      // Fallback to neutral color for any unknown status values
      // This ensures the cell still renders properly even with unexpected status strings
      return 'inherit';
  }
};

/**
 * Applies custom symbols to a formatted amount
 * @param formattedAmount - The already formatted amount string
 * @param symbol - Symbol string that may contain multiple symbols separated by '|'
 * @returns Formatted amount with symbols applied
 */
const applySymbolsToAmount = (formattedAmount: string, symbol: string): string => {
  let result = formattedAmount;
  const symbols = symbol.trim().split('|');

  symbols.forEach(s => {
    const trimmedSymbol = s.trim();
    if (trimmedSymbol.startsWith('suffix_')) {
      const bare = trimmedSymbol.replace('suffix_', '');
      result = `${result} ${bare}`;
    } else {
      result = `${trimmedSymbol} ${result}`;
    }
  });

  return result;
};

/**
 * Formats a numeric value based on format type and decimal places
 * @param numericValue - The numeric value to format
 * @param kpiAmount - Original KPI amount (for fallback)
 * @param kpiFormat - The format type (percentage, dollar, or default)
 * @param decimalPlaces - Optional number of decimal places
 * @returns Formatted string without custom symbols
 */
const formatByType = (
  numericValue: number,
  kpiAmount: number | string,
  kpiFormat: string,
  decimalPlaces?: number,
): string => {
  const shouldApplyDecimals = decimalPlaces !== undefined;

  switch (kpiFormat) {
    case KPI_FORMAT.PERCENTAGE:
      return shouldApplyDecimals
        ? formatNumberWithCommas(numericValue, true, decimalPlaces) + '%'
        : formatNumberWithCommas(kpiAmount) + '%';

    case KPI_FORMAT.DOLLAR: {
      if (shouldApplyDecimals) {
        return formatNegativeChangeValue(numericValue.toFixed(decimalPlaces), true);
      }
      return formatNegativeChangeValue(numericValue.toString(), true);
    }
    default:
      return shouldApplyDecimals
        ? formatNumberWithCommas(numericValue, true, decimalPlaces)
        : formatNumberWithCommas(kpiAmount);
  }
};

/**
 * Formats a KPI amount based on the specified format and symbol
 *
 * @param kpiAmount - The numeric KPI value to format
 * @param kpiFormat - The format type (percentage, dollar, or default)
 * @param symbol - Optional symbol to add as prefix or suffix (prefix by default, or use 'suffix_' prefix for suffix)
 * @param decimalPlaces - Optional number of decimal places to display (overrides default behavior)
 * @returns Formatted string representation of the KPI amount
 *
 * @example
 * formatKpiAmount(50, KPI_FORMAT.PERCENTAGE) // returns "50%"
 * formatKpiAmount(100, KPI_FORMAT.DOLLAR) // returns "$100"
 * formatKpiAmount(5, KPI_FORMAT.DEFAULT, "suffix_days") // returns "5 days"
 * formatKpiAmount(1234.567, KPI_FORMAT.DOLLAR, null, 0) // returns "$1,235"
 * formatKpiAmount(1234.567, KPI_FORMAT.DOLLAR, null, 2) // returns "$1,234.57"
 */
export const formatKpiAmount = (
  kpiAmount: number | string | null | undefined,
  kpiFormat: string,
  symbol?: string | null,
  decimalPlaces?: number,
): string => {
  // Check if kpiAmount is null, undefined, or empty string (but allow 0)
  if (kpiAmount === null || kpiAmount === undefined || kpiAmount === '') {
    return '-';
  }

  const numericValue = typeof kpiAmount === 'string' ? parseFloat(kpiAmount) : kpiAmount;
  let formattedAmount = formatByType(numericValue, kpiAmount, kpiFormat, decimalPlaces);

  if (symbol) {
    formattedAmount = applySymbolsToAmount(formattedAmount, symbol);
  }

  return formattedAmount;
};
/**
 * Renders a centered dash (-) for empty values (null, undefined, empty string, or 'N/A')
 * Returns null if the value is valid, allowing the caller to continue with their formatting logic
 * Used across DataGrid render functions for consistent empty cell display
 *
 * @param value - The value to check for emptiness
 * @returns JSX.Element with centered dash if empty, null otherwise
 *
 * @example
 * const emptyCheck = renderEmptyValue(params.value);
 * if (emptyCheck) return emptyCheck; // Early return if empty
 * // Continue with normal formatting...
 */
export const renderEmptyValue = (value: string | number | null | undefined): JSX.Element | null => {
  if (value === null || value === undefined || value === '' || value === 'N/A') {
    return React.createElement('div', { style: { textAlign: 'center', width: '100%' } }, '-');
  }
  return null;
};

/**
 * Renders Peer Group Rank cells with bold first number and normal second number
 * Splits rank string by "/" and applies fontWeight 500 to the first part (current rank)
 * Used across DataGrid Peer Group Rank columns for consistent formatting
 *
 * @param params - GridRenderCellParams from MUI DataGrid
 * @returns JSX.Element with formatted rank or dash if empty
 *
 * @example
 * // In DataGrid column definition:
 * renderCell: renderPeerGroupRankCell
 * // Input: "42/163" -> Output: <span><span style={{fontWeight: 500}}>42</span><span>/163</span></span>
 */
export const renderPeerGroupRankCell = (
  params: GridRenderCellParams<unknown, string>,
): JSX.Element => {
  const emptyCheck = renderEmptyValue(params.value);
  if (emptyCheck) return emptyCheck;

  // At this point, params.value is guaranteed to be a non-empty string
  const value = params.value ?? '';

  // Split rank string by "/" and trim whitespace (e.g., "42/163")
  const parts = value.split('/').map(part => part.trim());

  // Handle invalid format gracefully
  if (parts.length !== 2) {
    return React.createElement('span', null, value);
  }

  const [currentRank, totalRanks] = parts;

  return React.createElement(
    'span',
    null,
    React.createElement('span', { style: { fontWeight: 500 } }, currentRank),
    React.createElement('span', null, `/${totalRanks}`),
  );
};

/**
 * Formats a change value with proper negative sign placement for currency
 * Places negative sign before the currency symbol (e.g., -$1,234 instead of $-1,234)
 * Used across DataGrid "Change (Over Last Year)" columns for consistent formatting
 *
 * @param value - The numeric value as a string (may include commas)
 * @param isCurrency - Whether to include currency symbol
 * @returns Formatted string with proper negative sign placement
 *
 * @example
 * formatChangeValue("-1234.56", true)  // Returns: "-$1,234.56"
 * formatChangeValue("1234.56", true)   // Returns: "$1,234.56"
 * formatChangeValue("-1234", false)    // Returns: "-1,234"
 * formatChangeValue("N/A", true)       // Returns: "N/A"
 */
export const formatNegativeChangeValue = (value: string, isCurrency: boolean): string => {
  if (!value || value === 'N/A') return value;

  const cleanedValue = value.replace(/,/g, '');
  const numericValue = parseFloat(cleanedValue);

  // Check if value is negative
  const isNegative = numericValue < 0;

  // Remove negative sign but preserve decimal structure (e.g., "22.20" stays "22.20")
  const absValueStr = cleanedValue.startsWith('-') ? cleanedValue.slice(1) : cleanedValue;
  const formattedValue = formatNumberWithCommas(absValueStr);

  // Build display value with negative sign before currency symbol
  if (isCurrency) {
    return isNegative ? `-$${formattedValue}` : `$${formattedValue}`;
  }

  return isNegative ? `-${formattedValue}` : formattedValue;
};

/**
 * Computes a dynamic minWidth (in pixels) for a DataGrid column from the
 * largest absolute numeric value that will appear in that column.
 *
 * Works by counting how many characters the formatted number will have:
 * digits + commas (1 per 3 digits) + optional prefix ($, -, etc.).
 * Each character is estimated at 9px (conservative estimate for the 16px font used in
 * MUI DataGrid cells), plus 24px of cell padding.
 *
 * @param maxAbsValue - The largest absolute value that will appear in the column
 * @param prefix      - Any fixed prefix added to every cell, e.g. '$' or '-$'
 * @returns The computed minWidth in pixels
 *
 * @example
 * minWidthForNumber(1234567890, '$')  // → 150px  ($1,234,567,890 = 14 chars, 10 digits ≥ 9)
 * minWidthForNumber(12345678, '$')    // → 115.2px (8 digits < 9, stays at default floor)
 * minWidthForNumber(45.67, '%')       // → 115.2px (1 digit < 9, stays at default floor)
 */
export const minWidthForNumber = (maxAbsValue: number, prefix = ''): number => {
  const CHAR_WIDTH = 9; // estimated width in px of one character rendered at 16px font size
  const CELL_PADDING = 24; // extra px added to account for MUI DataGrid internal cell spacing
  const DEFAULT_MIN = 115.2;
  const digits = Math.max(1, Math.floor(Math.abs(maxAbsValue))).toString().length;
  if (digits < 9) return DEFAULT_MIN;
  const commas = Math.floor((digits - 1) / 3);
  const totalChars = digits + commas + prefix.length;
  return Math.max(DEFAULT_MIN, totalChars * CHAR_WIDTH + CELL_PADDING);
};


/**
 * Builds the store header info object used in print view pages.
 *
 * Single store: uses the current store's own name/city/state.
 * Multi-store:  uses the parent store's city/state with the current store number.
 *
 * @param userDetails - The user details object from Redux state
 * @param isMultiStoreEnabled - Whether multi-store mode is active
 * @returns Object with storeLine string and isMultiStore flag
 */
export const buildStoreHeaderInfo = (
  userDetails: UserDetails | null,
  isMultiStoreEnabled: boolean,
): { storeLine: string; isMultiStore: boolean } => {
  const chainList = userDetails?.userChainList ?? [];
  const currentStoreNumber = userDetails?.currentStoreNumber ?? 'N/A';
  const currentStore = chainList.find(s => s.storeNumber === currentStoreNumber);

  if (!isMultiStoreEnabled) {
    return {
      storeLine: currentStore
        ? `${currentStore.storeName} (#${currentStore.storeNumber}) ${currentStore.storeCity}, ${currentStore.storeState}`
        : `Ace Hardware (#${currentStoreNumber})`,
      isMultiStore: false,
    };
  }

  // Multi-store — find the parent store via userChainList[0].parentStore
  const parentStoreNumber = chainList[0]?.parentStore;
  const parentStore = chainList.find(s => s.storeNumber === parentStoreNumber);
  const locationStore = parentStore ?? currentStore;

  return {
    storeLine: locationStore
      ? `${locationStore.storeName} (#${currentStoreNumber}) ${locationStore.storeCity}, ${locationStore.storeState}`
      : `Ace Hardware (#${currentStoreNumber})`,
    isMultiStore: true,
  };
};

/**
 * Triggers a browser file-save for the provided Blob.
 *
 * @param blob - The Blob to save
 * @param fileName - The filename to suggest to the browser
 */
export const saveBlobAsFile = (blob: Blob, fileName: string): void => {
  const url = globalThis.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  globalThis.URL.revokeObjectURL(url);
};
